import { useState, type FormEvent } from 'react';
import {
  ACTIVE_DIRECTORY_PRESET,
  OPENLDAP_PRESET,
  type LdapConfig,
  type LdapSettingsResponse,
  type LdapSyncResult,
  type LdapTestResult,
  type UpdateLdapConfigInput,
} from '@orbit-sync/shared-types';
import { FormBanner } from '../../components/FormBanner.js';
import { Icon } from '../../components/Icon.js';
import { api, errorMessage, fieldErrorsFrom, type FieldErrors } from '../../lib/api/index.js';
import { formatDate } from '../adminHelpers.js';
import { useAdminQuery } from '../useAdminQuery.js';

/** Directory (LDAP / Active Directory) sign-in: servers, mapping, test and sync. */
export function AdminDirectoryPage() {
  const { data, error, reload } = useAdminQuery((signal) => api.admin.getLdap(signal), 'ldap');

  return (
    <section className="admin-page">
      <header className="admin-head">
        <div>
          <h1>Directory sign-in</h1>
          <p className="muted small">
            Let people sign in with their Active Directory (or any LDAP) account and find each other
            here. Standard LDAPv3 over LDAPS or StartTLS; passwords are checked by the directory and
            never stored.
          </p>
        </div>
      </header>
      <FormBanner message={error} />
      {data ? (
        <DirectoryForm key={JSON.stringify(data.config)} initial={data} onSynced={reload} />
      ) : null}
    </section>
  );
}

function DirectoryForm({
  initial,
  onSynced,
}: {
  initial: LdapSettingsResponse;
  onSynced: () => void;
}) {
  const [draft, setDraft] = useState<LdapConfig>(initial.config);
  const [hasPassword, setHasPassword] = useState(initial.hasBindPassword);
  /** `undefined` keeps the stored password, `''` clears it, anything else replaces it. */
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [urlsText, setUrlsText] = useState(initial.config.urls.join('\n'));
  const [attributesText, setAttributesText] = useState(initial.config.loginAttributes.join(', '));
  const [busy, setBusy] = useState<'save' | 'test' | 'sync' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saved, setSaved] = useState(false);
  const [test, setTest] = useState<LdapTestResult | null>(null);
  const [testUser, setTestUser] = useState('');
  const [testPassword, setTestPassword] = useState('');
  const [lastSync, setLastSync] = useState<LdapSyncResult | null>(initial.lastSync);

  const set = <K extends keyof LdapConfig>(key: K, value: LdapConfig[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
  };

  function input(): UpdateLdapConfigInput {
    return {
      ...draft,
      urls: urlsText
        .split(/[\s,]+/)
        .map((url) => url.trim())
        .filter(Boolean),
      loginAttributes: attributesText
        .split(/[\s,]+/)
        .map((attribute) => attribute.trim())
        .filter(Boolean),
      ...(password === undefined ? {} : { bindPassword: password === '' ? null : password }),
    };
  }

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy('save');
    setError(null);
    setFieldErrors({});

    try {
      const result = await api.admin.updateLdap(input());
      setDraft(result.config);
      setHasPassword(result.hasBindPassword);
      setPassword(undefined);
      setSaved(true);
    } catch (cause) {
      setFieldErrors(fieldErrorsFrom(cause));
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  async function runTest(): Promise<void> {
    setBusy('test');
    setError(null);
    setTest(null);

    try {
      setTest(
        await api.admin.testLdap({
          config: input(),
          ...(testUser.trim() && testPassword
            ? { testLogin: { username: testUser.trim(), password: testPassword } }
            : {}),
        }),
      );
    } catch (cause) {
      setFieldErrors(fieldErrorsFrom(cause));
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
      setTestPassword('');
    }
  }

  async function runSync(): Promise<void> {
    setBusy('sync');
    setError(null);

    try {
      setLastSync(await api.admin.syncLdap());
      onSynced();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  const fieldError = (key: string) =>
    Object.entries(fieldErrors)
      .filter(
        ([path]) => path === key || path.startsWith(`${key}.`) || path.startsWith(`config.${key}`),
      )
      .map(([path, message]) => (
        <p key={path} className="field-error">
          {message}
        </p>
      ));

  const text = (
    key:
      | 'name'
      | 'bindDn'
      | 'baseDn'
      | 'userFilter'
      | 'requiredGroupDn'
      | 'adminGroupDn'
      | 'operatorGroupDn'
      | 'uniqueIdAttribute'
      | 'emailAttribute'
      | 'displayNameAttribute',
    label: string,
    hint: string,
    placeholder = '',
  ) => (
    <div className="field">
      <label htmlFor={`ldap-${key}`}>{label}</label>
      <input
        id={`ldap-${key}`}
        value={draft[key]}
        placeholder={placeholder}
        spellCheck={false}
        onChange={(event) => set(key, event.target.value)}
      />
      <p className="field-hint">{hint}</p>
      {fieldError(key)}
    </div>
  );

  const toggle = (
    key:
      | 'enabled'
      | 'verifyCertificate'
      | 'nestedGroups'
      | 'autoCreateUsers'
      | 'newUsersDisabled'
      | 'linkExistingAccounts',
    label: string,
    hint: string,
  ) => (
    <label className="setting-toggle">
      <input
        type="checkbox"
        checked={draft[key]}
        onChange={(event) => set(key, event.target.checked)}
      />
      <span className="switch" aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        <span className="field-hint">{hint}</span>
      </span>
    </label>
  );

  return (
    <form className="settings-form" onSubmit={(event) => void save(event)} noValidate>
      <FormBanner message={error} />

      <fieldset className="admin-panel">
        <legend>Sign-in</legend>
        {toggle(
          'enabled',
          'Allow directory sign-in',
          'People type their directory user name (jdoe, CORP\\jdoe or jdoe@corp.example) and password on the normal sign-in page.',
        )}
        {text('name', 'Shown as', 'What the sign-in page calls the directory.', 'Active Directory')}
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Servers</legend>
        <div className="field">
          <label htmlFor="ldap-urls">Directory servers</label>
          <textarea
            id="ldap-urls"
            value={urlsText}
            spellCheck={false}
            placeholder={'ldaps://dc1.corp.example.com:636\nldaps://dc2.corp.example.com:636'}
            onChange={(event) => {
              setUrlsText(event.target.value);
              setSaved(false);
            }}
          />
          <p className="field-hint">
            One per line, tried in order; the next is used only when one cannot be reached.
          </p>
          {fieldError('urls')}
        </div>
        <div className="field">
          <label htmlFor="ldap-security">Connection security</label>
          <select
            id="ldap-security"
            value={draft.security}
            onChange={(event) => set('security', event.target.value as LdapConfig['security'])}
          >
            <option value="ldaps">LDAPS - TLS from the start (ldaps://, port 636)</option>
            <option value="starttls">
              StartTLS - upgrade before signing in (ldap://, port 389)
            </option>
            <option value="none">None - passwords in clear text (lab use only)</option>
          </select>
          {draft.security === 'none' ? (
            <p className="field-error">
              Passwords would cross the network unencrypted. Use LDAPS or StartTLS.
            </p>
          ) : null}
        </div>
        {toggle(
          'verifyCertificate',
          'Verify the server certificate',
          'Turn off only to diagnose a certificate problem; never leave it off.',
        )}
        <div className="field">
          <label htmlFor="ldap-ca">CA certificate (PEM, optional)</label>
          <textarea
            id="ldap-ca"
            value={draft.caCertificate}
            spellCheck={false}
            placeholder="-----BEGIN CERTIFICATE-----"
            onChange={(event) => set('caCertificate', event.target.value)}
          />
          <p className="field-hint">
            Your internal CA, if the domain controllers' certificates are issued by one. Empty
            trusts the server's system CAs.
          </p>
        </div>
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Admin panel access</legend>
        <p className="field-hint">
          Members of these groups sign in to this admin panel with their directory username and
          password. The panel's own administrator account keeps working. Leave both empty to allow
          nobody from the directory.
        </p>
        {text(
          'adminGroupDn',
          'Administrators group',
          'Full access to everything in the admin panel.',
          'CN=Orbit Admins,OU=Groups,DC=corp,DC=example,DC=com',
        )}
        {text(
          'operatorGroupDn',
          'Operators group',
          'Can see the dashboard and users, and enable or disable accounts - nothing else.',
          'CN=Orbit Operators,OU=Groups,DC=corp,DC=example,DC=com',
        )}
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Service account</legend>
        {text(
          'bindDn',
          'Bind DN or user principal name',
          'A read-only account that searches the directory. Empty searches anonymously.',
          'CN=svc-orbit,OU=Service Accounts,DC=corp,DC=example,DC=com',
        )}
        <div className="field">
          <label htmlFor="ldap-password">Password</label>
          <input
            id="ldap-password"
            type="password"
            autoComplete="new-password"
            value={password ?? ''}
            placeholder={hasPassword ? '•••••••• (stored - leave empty to keep)' : ''}
            onChange={(event) => {
              setPassword(event.target.value === '' ? undefined : event.target.value);
              setSaved(false);
            }}
          />
          <p className="field-hint">
            Stored encrypted and never shown again.
            {hasPassword ? (
              <>
                {' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setPassword('');
                    setHasPassword(false);
                    setSaved(false);
                  }}
                >
                  Remove the stored password
                </button>
              </>
            ) : null}
          </p>
        </div>
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Users</legend>
        <div className="ldap-actions">
          <span className="field-hint">Presets:</span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setDraft((current) => ({ ...current, ...ACTIVE_DIRECTORY_PRESET }));
              setAttributesText(ACTIVE_DIRECTORY_PRESET.loginAttributes.join(', '));
            }}
          >
            Active Directory
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setDraft((current) => ({ ...current, ...OPENLDAP_PRESET }));
              setAttributesText(OPENLDAP_PRESET.loginAttributes.join(', '));
            }}
          >
            OpenLDAP
          </button>
        </div>
        {text(
          'baseDn',
          'Search base',
          'Where users are searched for, and everything below it.',
          'DC=corp,DC=example,DC=com',
        )}
        {text(
          'userFilter',
          'User filter',
          'An LDAP filter every user must match. The Active Directory default leaves out disabled accounts.',
        )}
        {text(
          'requiredGroupDn',
          'Only members of this group (optional)',
          'The group DN. Members may sign in and are imported; nobody else is.',
          'CN=Orbit Users,OU=Groups,DC=corp,DC=example,DC=com',
        )}
        {toggle(
          'nestedGroups',
          'Include nested group membership',
          'Active Directory only. Turn off for other directories.',
        )}
        <div className="field">
          <label htmlFor="ldap-login-attributes">Sign in with</label>
          <input
            id="ldap-login-attributes"
            value={attributesText}
            spellCheck={false}
            onChange={(event) => {
              setAttributesText(event.target.value);
              setSaved(false);
            }}
          />
          <p className="field-hint">Attributes a typed name is matched against, comma-separated.</p>
          {fieldError('loginAttributes')}
        </div>
        <div className="field-row">
          {text('uniqueIdAttribute', 'Unique id attribute', 'Never changes for a person.')}
          {text('emailAttribute', 'Email attribute', 'Falls back to the user principal name.')}
          {text('displayNameAttribute', 'Name attribute', 'Shown in chats.')}
        </div>
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Accounts</legend>
        {toggle(
          'autoCreateUsers',
          'Create accounts for directory users',
          'On first sign-in and on every sync. When off, only accounts that already exist here can use the directory.',
        )}
        {toggle(
          'newUsersDisabled',
          'New directory accounts start disabled',
          'An administrator enables each one (Users) before it can sign in. Linked existing accounts are not affected.',
        )}
        {toggle(
          'linkExistingAccounts',
          'Link existing accounts by email',
          'A local account with the same email becomes the directory user’s, keeping its conversations; its local password stops working.',
        )}
        <div className="field">
          <label htmlFor="ldap-sync">Import the directory every (minutes)</label>
          <input
            id="ldap-sync"
            type="number"
            min={0}
            max={10_080}
            value={draft.syncIntervalMinutes}
            onChange={(event) => set('syncIntervalMinutes', Number(event.target.value))}
          />
          <p className="field-hint">
            Imports everyone so colleagues can find each other before their first sign-in, keeps
            names and emails current, and disables accounts that left the directory. 0 imports only
            when you press Sync now.
          </p>
        </div>
        <div className="ldap-actions">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={busy !== null || !initial.config.enabled}
            title={initial.config.enabled ? undefined : 'Save with directory sign-in on first'}
            onClick={() => void runSync()}
          >
            {busy === 'sync' ? <span className="btn-spinner" /> : <Icon name="rotate" size={14} />}
            Sync now
          </button>
          {lastSync ? (
            <SyncSummary result={lastSync} />
          ) : (
            <span className="field-hint">Never synced.</span>
          )}
        </div>
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Test</legend>
        <p className="field-hint">
          Tries the settings above - saved or not - without changing anything. Add a user to check
          their sign-in too; the password is used once and not kept.
        </p>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ldap-test-user">User name (optional)</label>
            <input
              id="ldap-test-user"
              value={testUser}
              autoComplete="off"
              onChange={(event) => setTestUser(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ldap-test-password">Their password</label>
            <input
              id="ldap-test-password"
              type="password"
              autoComplete="off"
              value={testPassword}
              onChange={(event) => setTestPassword(event.target.value)}
            />
          </div>
        </div>
        <div className="ldap-actions">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={busy !== null}
            onClick={() => void runTest()}
          >
            {busy === 'test' ? <span className="btn-spinner" /> : <Icon name="zap" size={14} />}
            Test connection
          </button>
        </div>
        {test ? <TestReport result={test} /> : null}
      </fieldset>

      <div className="settings-actions">
        {saved ? (
          <span className="admin-notice">
            <Icon name="check" size={14} />
            Saved
          </span>
        ) : null}
        <button type="submit" className="btn btn--primary" disabled={busy !== null}>
          {busy === 'save' ? <span className="btn-spinner" /> : <Icon name="check" size={14} />}
          Save directory settings
        </button>
      </div>
    </form>
  );
}

function TestReport({ result }: { result: LdapTestResult }) {
  return (
    <div className="ldap-report">
      <p className={result.ok ? 'admin-notice' : 'field-error'}>
        <Icon name={result.ok ? 'check' : 'alertCircle'} size={14} />
        {result.ok ? 'Everything worked.' : 'Something failed - see below.'}
      </p>
      <ul className="ldap-steps">
        {result.steps.map((step, index) => (
          <li
            key={index}
            className={step.ok ? 'ldap-step ldap-step--ok' : 'ldap-step ldap-step--failed'}
          >
            <Icon name={step.ok ? 'check' : 'x'} size={14} />
            <span>
              <strong>{step.label}</strong> - {step.detail}
            </span>
          </li>
        ))}
      </ul>
      {result.sampleUsers.length > 0 ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>DN</th>
              </tr>
            </thead>
            <tbody>
              {result.sampleUsers.map((user) => (
                <tr key={user.dn}>
                  <td dir="auto">{user.displayName ?? '—'}</td>
                  <td>{user.email ?? '— (cannot be imported)'}</td>
                  <td className="truncate">{user.dn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function SyncSummary({ result }: { result: LdapSyncResult }) {
  return (
    <span className="field-hint">
      {result.ok ? (
        <>
          Last sync {formatDate(result.finishedAt, true)}: {result.found} found, {result.created}{' '}
          created, {result.updated} updated, {result.linked} linked, {result.disabled} disabled,{' '}
          {result.reenabled} re-enabled, {result.skipped} skipped.
          {result.notes.length > 0 ? ` ${result.notes.slice(0, 3).join(' · ')}` : ''}
        </>
      ) : (
        <>
          Last sync {formatDate(result.finishedAt, true)} failed: {result.error}
        </>
      )}
    </span>
  );
}
